import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import FeeScheduleCategoryCommand from "marketplace/teams/fee-schedules/_api/commands/fee-schedule-category-command";
import Session from "users/__session/session";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedFeeSchedule } from "work/masked-fields";
import ReplaceableDocumentAPIRequest from "work/proposals/_api/commands/replaceable-document-api-request";
import WorkProposalAPIRequest, {
  toProposalRedlineFieldAPI,
} from "work/proposals/_api/commands/work-proposal-api-request";
import WorkTeamAPICommand from "work/proposals/_api/commands/work-team-api-command";
import Proposal from "work/proposals/proposal";
import ProposalReviewerCommand from "work/proposals/reviewers/_api/command-contracts/proposal-reviewer-command";

class UpdateProposalAPIRequest {
  public payload: Array<{ op: string; path: string; value: unknown }> = [];

  public constructor(params: { originalProposal: Proposal; proposal: Proposal; session: Session }) {
    const { originalProposal, proposal, session } = params;
    const updatedProposal = proposal;
    this.session = session;

    const reviewerEditRightsChanged = this.proposalReviewerEditRightsDiffer(updatedProposal, originalProposal);
    this.populateProposalReviewerDiffPatches(updatedProposal, originalProposal);
    if (!updatedProposal.isSubmitted) {
      this.populateProposalPATCHOperations(this.getDraftProposalPatchSource(updatedProposal), originalProposal);
      return;
    }

    if (reviewerEditRightsChanged || this.hasPartyRevisionChangesToSave(updatedProposal, originalProposal)) {
      this.populatePartyRevisionPATCHOperations(updatedProposal, originalProposal);
    }
  }

  private readonly session: Session;

  private getDraftProposalPatchSource(updatedProposal: Proposal): Proposal {
    if (updatedProposal.supersedes && updatedProposal.redlinedRevision) {
      return updatedProposal.redlinedRevision;
    }

    return updatedProposal;
  }

  private hasPartyRevisionChangesToSave(updatedProposal: Proposal, originalProposal: Proposal): boolean {
    if (this.hasRedlineChangesToSave(updatedProposal)) {
      return true;
    }
    if (
      typeof updatedProposal.isEquivalentTo === "function" &&
      !updatedProposal.isEquivalentTo(originalProposal)
    ) {
      return true;
    }
    return false;
  }

  private hasRedlineChangesToSave(proposal: Proposal): boolean {
    if (this.hasLocallyUnsavedRedlineChanges(proposal)) {
      return true;
    }

    const redline = proposal.redline;
    if (!redline) return false;

    const persistedRedline = proposal.persistedRedline;
    if (persistedRedline && typeof redline.isEqualTo === "function") {
      return !redline.isEqualTo(persistedRedline);
    }

    return Boolean(redline.hasChanges);
  }

  private hasLocallyUnsavedRedlineChanges(proposal: Proposal): boolean {
    return Boolean(
      (proposal as Proposal & { hasLocallyUnsavedRedlineChanges?: boolean }).hasLocallyUnsavedRedlineChanges,
    );
  }

  private populatePartyRevisionPATCHOperations(updatedProposal: Proposal, originalProposal: Proposal) {
    if (!this.session.user) {
      throw new UpdateProposalAPIRequestError();
    }

    const redlinedRevision = updatedProposal.redlinedRevision;
    if (!redlinedRevision) return;

    const existingPartyRevisionSpec = this.getCurrentPartyRevisionSpecForPatch(originalProposal);

    if (!existingPartyRevisionSpec) {
      this.payload = [
        {
          op: "replace",
          path: "/partyRevision",
          // Serialize nested revisions to match the API's full-replacement patch contract.
          value: JSON.stringify(
            new WorkProposalAPIRequest(redlinedRevision, this.session, updatedProposal.redlining?.unresolvedFields)
              .payload,
          ),
        },
        ...this.payload,
      ];
      return;
    }

    const existingPartyRevision = new Proposal({
      currentUser: this.session.user,
      spec: {
        ...existingPartyRevisionSpec,
        clientReviewers: existingPartyRevisionSpec.clientReviewers ?? [],
        vendorReviewers: existingPartyRevisionSpec.vendorReviewers ?? [],
      },
    });

    const payloadLengthBeforePartyRevisionPatch = this.payload.length;
    this.populateProposalReviewerDiffPatches(redlinedRevision, existingPartyRevision, "/partyRevision");
    this.populateProposalPATCHOperations(redlinedRevision, existingPartyRevision, "/partyRevision");
    if (
      this.payload.length === payloadLengthBeforePartyRevisionPatch &&
      this.hasLocallyUnsavedRedlineChanges(updatedProposal)
    ) {
      this.payload = [{ op: "replace", path: "/partyRevision/forceUpdate", value: true }, ...this.payload];
    }
  }

  private getCurrentPartyRevisionSpecForPatch(proposal: Proposal): Proposal["partyRevision"] {
    if (!proposal.partyRevision) return undefined;
    if (!proposal.supersedes) return proposal.partyRevision;
    if (this.proposalHasLocalCounterRevisionMarker(proposal)) return proposal.partyRevision;
    if (this.proposalHasCurrentRevisionNegotiationOperations(proposal)) return proposal.partyRevision;

    return undefined;
  }

  private proposalHasLocalCounterRevisionMarker(proposal: Proposal): boolean {
    return Boolean(
      (proposal as Proposal & {
        hasLocallyMaterializedCounterRevision?: boolean;
        hasLocallyUnsavedRedlineChanges?: boolean;
      }).hasLocallyMaterializedCounterRevision ||
        (proposal as Proposal & { hasLocallyUnsavedRedlineChanges?: boolean }).hasLocallyUnsavedRedlineChanges,
    );
  }

  private proposalHasCurrentRevisionNegotiationOperations(proposal: Proposal): boolean {
    const revisionId = proposal.id?.value ?? proposal.latestRevisionId?.value;
    return Boolean(
      revisionId &&
        proposal.negotiationOperations.some(
          (operation) =>
            operation.baseRevisionId === revisionId &&
            (operation.kind === "PartyRevisionPatched" || operation.kind === "PartyRevisionReplaced"),
        ),
    );
  }

  private populateProposalPATCHOperations(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix = "",
  ): void {
    if (updatedProposal.name?.value !== originalProposal.name?.value)
      this.payload = [{ op: "replace", path: `${pathPrefix}/name`, value: updatedProposal.name }, ...this.payload];

    if (updatedProposal.description?.value !== originalProposal.description?.value)
      this.payload = [
        { op: "replace", path: `${pathPrefix}/description`, value: updatedProposal.description },
        ...this.payload,
      ];

    if (!updatedProposal.negotiable.isEqualTo(originalProposal.negotiable)) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/negotiable`, value: updatedProposal.negotiable.value },
        ...this.payload,
      ];
    }

    if (
      (updatedProposal.unresolvedFields && !originalProposal.unresolvedFields) ||
      (!updatedProposal.unresolvedFields && originalProposal.unresolvedFields) ||
      _.xor(Array.from(updatedProposal.unresolvedFields ?? []), Array.from(originalProposal.unresolvedFields ?? []))
        .length > 0
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/unresolvedFields`,
          value: Array.from(new Set(Array.from(updatedProposal.unresolvedFields ?? []).map(toProposalRedlineFieldAPI))),
        },
        ...this.payload,
      ];
    }

    this.populateProposalDateDiffPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateProposalTeamDiffPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateFeeScheduleDiffPatches(originalProposal.feeSchedule, updatedProposal.feeSchedule, pathPrefix);
    this.populateProposalContractsDiffPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateProposalDiscountDiffPatch(updatedProposal, originalProposal, pathPrefix);
    this.populateProposalTemplateDiffPatches(updatedProposal, originalProposal, pathPrefix);

    if (this.payload.length === 0 && updatedProposal.lastUpdated?.isAfter(originalProposal.lastUpdated)) {
      this.payload = [{ op: "replace", path: `${pathPrefix}/forceUpdate`, value: true }, ...this.payload];
    }
  }

  private populateProposalDateDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (
      (updatedProposal.responseDueBy || originalProposal.responseDueBy) &&
      !updatedProposal.responseDueBy?.isSame(originalProposal.responseDueBy)
    ) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/responseDueBy`, value: updatedProposal.responseDueBy },
        ...this.payload,
      ];
    }

    if (
      (updatedProposal.startDate || originalProposal.startDate) &&
      !updatedProposal.startDate?.isSame(originalProposal.startDate)
    ) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/startDate`, value: updatedProposal.startDate },
        ...this.payload,
      ];
    }

    if (
      (updatedProposal.endDate || originalProposal.endDate) &&
      !updatedProposal.endDate?.isSame(originalProposal.endDate)
    ) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/endDate`, value: updatedProposal.endDate },
        ...this.payload,
      ];
    }
  }

  private populateProposalReviewerDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix = "",
  ): void {
    if (
      updatedProposal.clientReviewers.length !== originalProposal.clientReviewers.length ||
      !updatedProposal.clientReviewers.every((updatedReviewer) =>
        originalProposal.clientReviewers.some((originalReviewer) => originalReviewer.isEqualTo(updatedReviewer)),
      )
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/clientReviewers`,
          value: updatedProposal.clientReviewers.map((reviewer) => new ProposalReviewerCommand(reviewer).payload),
        },
        ...this.payload,
      ];
    }

    if (
      updatedProposal.vendorReviewers.length !== originalProposal.vendorReviewers.length ||
      !updatedProposal.vendorReviewers.every((updatedReviewer) =>
        originalProposal.vendorReviewers.some((originalReviewer) => originalReviewer.isEqualTo(updatedReviewer)),
      )
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/vendorReviewers`,
          value: updatedProposal.vendorReviewers.map((reviewer) => new ProposalReviewerCommand(reviewer).payload),
        },
        ...this.payload,
      ];
    }
  }

  private proposalReviewerEditRightsDiffer(updatedProposal: Proposal, originalProposal: Proposal): boolean {
    return [
      [updatedProposal.clientReviewers, originalProposal.clientReviewers],
      [updatedProposal.vendorReviewers, originalProposal.vendorReviewers],
    ].some(([updatedReviewers, originalReviewers]) =>
      updatedReviewers.some((updatedReviewer) => {
        const originalReviewer = originalReviewers.find((reviewer) =>
          reviewer.userId.isEqualTo(updatedReviewer.userId),
        );
        return originalReviewer !== undefined && originalReviewer.isEditing !== updatedReviewer.isEditing;
      }),
    );
  }

  private populateProposalTeamDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (
      Masked.isPresentNotMasked(updatedProposal.clientTeam) &&
      Masked.isNotMasked(originalProposal.clientTeam) &&
      updatedProposal.clientTeam.leader &&
      !updatedProposal.clientTeam.leader?.isEqualTo(originalProposal.clientTeam?.leader)
    ) {
      const update = {
        op: "replace",
        path: `${pathPrefix}/clientTeam`,
        value: WorkTeamAPICommand.fromTeam(updatedProposal.clientTeam).payload,
      };
      this.payload = this.payload?.length ? this.payload.concat(update) : [update];
    }
    if (
      updatedProposal.vendorTeam &&
      Masked.isNotMasked(updatedProposal.vendorTeam) &&
      Masked.isNotMasked(originalProposal.vendorTeam) &&
      !updatedProposal.vendorTeam.isEqualTo(originalProposal.vendorTeam)
    ) {
      const update = {
        op: "replace",
        path: `${pathPrefix}/vendorTeam`,
        value: WorkTeamAPICommand.fromTeam(updatedProposal.vendorTeam).payload,
      };
      this.payload = this.payload?.length ? this.payload.concat(update) : [update];
    }
    if (
      Masked.isNotMasked(updatedProposal.teamRestricted) &&
      Masked.isNotMasked(originalProposal.teamRestricted) &&
      !updatedProposal.teamRestricted.isEqualTo(originalProposal.teamRestricted)
    ) {
      const update = {
        op: "replace",
        path: `${pathPrefix}/teamRestricted`,
        value: updatedProposal.teamRestricted,
      };
      this.payload = this.payload?.length ? this.payload.concat(update) : [update];
    }
  }

  private populateFeeScheduleDiffPatches(
    originalFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule,
    updatedFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule,
    pathPrefix: string,
  ): void {
    if (Masked.isMasked(originalFeeSchedule) || Masked.isMasked(updatedFeeSchedule)) {
      return;
    }
    if (
      updatedFeeSchedule.length === originalFeeSchedule.length &&
      updatedFeeSchedule.every((updatedCategory) => {
        const existingCategory = originalFeeSchedule.find((originalCategory) =>
          originalCategory.isEqualTo(updatedCategory),
        );
        return existingCategory?.isEqualTo(updatedCategory);
      })
    ) {
      return;
    }

    if (updatedFeeSchedule.length === 0) {
      this.payload = [{ op: "replace", path: `${pathPrefix}/feeSchedule`, value: [] }, ...this.payload];
      return;
    }

    for (const updatedCategory of updatedFeeSchedule) {
      const existingCategory = originalFeeSchedule.find((origCategory) =>
        origCategory.id.isEqualTo(updatedCategory.id),
      );

      if (!existingCategory) {
        this.payload = [
          {
            op: "add",
            path: `${pathPrefix}/feeSchedule`,
            value: new FeeScheduleCategoryCommand(updatedCategory).payload,
          },
          ...this.payload,
        ];
        continue;
      }

      if (existingCategory.isEqualTo(updatedCategory)) continue;

      if (
        FeeScheduleCategoryMaskedCurrency.isNotMasked(updatedCategory) &&
        FeeScheduleCategoryMaskedCurrency.isNotMasked(existingCategory)
      ) {
        const update = {
          op: "replace",
          path: `${pathPrefix}/feeSchedule/${existingCategory.id.value}`,
          value: new FeeScheduleCategoryCommand(updatedCategory).payload,
        };
        this.payload = this.payload?.length ? this.payload.concat(update) : [update];
      } else {
        this.populateFeeScheduleSubfieldDiffPatches(existingCategory, updatedCategory, pathPrefix);
      }
    }

    for (const removedCategory of originalFeeSchedule.filter(
      (originalCategory) =>
        !updatedFeeSchedule.some((updatedCategory) => updatedCategory.id.isEqualTo(originalCategory.id)),
    )) {
      this.payload = [
        {
          op: "remove",
          path: `${pathPrefix}/feeSchedule/${removedCategory.id.value}`,
          value: null,
        },
        ...this.payload,
      ];
    }
  }

  private populateFeeScheduleSubfieldDiffPatches(
    existingCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
    updatedCategory: FeeScheduleCategoryMaskedCurrency,
    pathPrefix: string,
  ): void {
    if (!existingCategory.name.isEqualTo(updatedCategory.name)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/feeSchedule/${existingCategory.id.value}/name`,
          value: updatedCategory.name,
        },
        ...this.payload,
      ];
    }

    if (
      (existingCategory.description && !updatedCategory.description) ||
      (!existingCategory.description && updatedCategory.description) ||
      !existingCategory.description?.isEqualTo(updatedCategory.description)
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/feeSchedule/${existingCategory.id.value}/description`,
          value: updatedCategory.description,
        },
        ...this.payload,
      ];
    }

    if (
      (existingCategory.billingCode && !updatedCategory.billingCode) ||
      (!existingCategory.billingCode && updatedCategory.billingCode) ||
      !existingCategory.billingCode?.isEqualTo(updatedCategory.billingCode)
    ) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/feeSchedule/${existingCategory.id.value}/billingCode`,
          value: updatedCategory.billingCode,
        },
        ...this.payload,
      ];
    }
  }

  private populateProposalContractsDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    this.populateClientPolicyDiffPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateVendorPolicyDiffPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateConflictsChecksPatches(updatedProposal, originalProposal, pathPrefix);
    this.populateConflictsChecksWaivedPatch(updatedProposal, originalProposal, pathPrefix);
  }

  private populateConflictsChecksWaivedPatch(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ) {
    if (
      Masked.isMasked(updatedProposal.conflictsCheckWaived) ||
      Masked.isMasked(originalProposal.conflictsCheckWaived)
    ) {
      return;
    }
    if (updatedProposal.conflictsCheckWaived.isEqualTo(originalProposal.conflictsCheckWaived)) {
      return;
    }

    this.payload = [
      {
        op: "replace",
        path: `${pathPrefix}/conflictsCheckWaived`,
        value: updatedProposal.conflictsCheckWaived,
      },
      ...this.payload,
    ];
  }

  private populateConflictsChecksPatches(updatedProposal: Proposal, originalProposal: Proposal, pathPrefix: string) {
    const updatedConflicts = updatedProposal.conflicts;
    const originalConflicts = originalProposal.conflicts;

    if (Masked.isMasked(updatedConflicts) || Masked.isMasked(originalConflicts)) {
      return;
    }
    if (
      updatedConflicts.some((conflict) => Masked.isMasked(conflict)) ||
      originalConflicts.some((conflict) => Masked.isMasked(conflict))
    ) {
      return;
    }
    if (
      originalConflicts.length === updatedConflicts.length &&
      updatedConflicts.every((updatedConflict) =>
        originalConflicts.some((originalConflict) => originalConflict.isEqualTo(updatedConflict)),
      )
    ) {
      return;
    }

    this.payload = [
      {
        op: "replace",
        path: `${pathPrefix}/conflicts`,
        value: updatedConflicts.map(
          (replaceAbleDocument) => new ReplaceableDocumentAPIRequest(replaceAbleDocument).payload,
        ),
      },
      ...this.payload,
    ];
  }

  private populateVendorPolicyDiffPatches(updatedProposal: Proposal, originalProposal: Proposal, pathPrefix: string) {
    const updatedVendorPolicies = updatedProposal.vendorPolicies;
    const originalVendorPolicies = originalProposal.vendorPolicies;

    if (Masked.isMasked(updatedVendorPolicies) || Masked.isMasked(originalVendorPolicies)) {
      return;
    }
    if (
      updatedVendorPolicies.some((policy) => Masked.isMasked(policy)) ||
      originalVendorPolicies.some((policy) => Masked.isMasked(policy))
    ) {
      return;
    }
    if (
      originalVendorPolicies.length === updatedVendorPolicies.length &&
      updatedVendorPolicies.every((updatedPolicy) =>
        originalVendorPolicies.some((originalPolicy) => originalPolicy.isEqualTo(updatedPolicy)),
      )
    ) {
      return;
    }

    this.payload = [
      {
        op: "replace",
        path: `${pathPrefix}/vendorPolicies`,
        value: updatedVendorPolicies.map(
          (replaceAbleDocument) => new ReplaceableDocumentAPIRequest(replaceAbleDocument).payload,
        ),
      },
      ...this.payload,
    ];
  }

  private populateClientPolicyDiffPatches(updatedProposal: Proposal, originalProposal: Proposal, pathPrefix: string) {
    const updatedClientPolicies = updatedProposal.clientPolicies;
    const originalClientPolicies = originalProposal.clientPolicies;

    if (Masked.isMasked(updatedClientPolicies) || Masked.isMasked(originalClientPolicies)) {
      return;
    }
    if (
      updatedClientPolicies.some((policy) => Masked.isMasked(policy)) ||
      originalClientPolicies.some((policy) => Masked.isMasked(policy))
    ) {
      return;
    }
    if (
      originalClientPolicies.length === updatedClientPolicies.length &&
      updatedClientPolicies.every((updatedPolicy) =>
        originalClientPolicies.some((originalPolicy) => originalPolicy.isEqualTo(updatedPolicy)),
      )
    ) {
      return;
    }

    this.payload = [
      {
        op: "replace",
        path: `${pathPrefix}/clientPolicies`,
        value: updatedClientPolicies.map(
          (replaceAbleDocument) => new ReplaceableDocumentAPIRequest(replaceAbleDocument).payload,
        ),
      },
      ...this.payload,
    ];
  }

  private populateProposalDiscountDiffPatch(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (
      Masked.isNotMasked(updatedProposal.discount) &&
      Masked.isNotMasked(originalProposal.discount) &&
      !updatedProposal.discount.isEqualTo(originalProposal.discount)
    ) {
      this.payload = [
        { op: "replace", path: `${pathPrefix}/discount`, value: updatedProposal.discount },
        ...this.payload,
      ];
    }
  }

  private populateProposalTemplateDiffPatches(
    updatedProposal: Proposal,
    originalProposal: Proposal,
    pathPrefix: string,
  ): void {
    if (!_.isEqual(updatedProposal.clientTeamTemplateIds, originalProposal.clientTeamTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/clientTeamTemplateIds`,
          value: updatedProposal.clientTeamTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.vendorTeamTemplateIds, originalProposal.vendorTeamTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/vendorTeamTemplateIds`,
          value: updatedProposal.vendorTeamTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.clientFeeScheduleTemplateIds, originalProposal.clientFeeScheduleTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/clientFeeScheduleTemplateIds`,
          value: updatedProposal.clientFeeScheduleTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.vendorFeeScheduleTemplateIds, originalProposal.vendorFeeScheduleTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/vendorFeeScheduleTemplateIds`,
          value: updatedProposal.vendorFeeScheduleTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }

    if (!_.isEqual(updatedProposal.vendorFeeScheduleTemplateIds, originalProposal.vendorFeeScheduleTemplateIds)) {
      this.payload = [
        {
          op: "replace",
          path: `${pathPrefix}/vendorFeeScheduleTemplateIds`,
          value: updatedProposal.vendorFeeScheduleTemplateIds.map((id: Guid) => id.value),
        },
        ...this.payload,
      ];
    }
  }
}

/**
 * Error class for UpdateProposalAPIRequest creation errors, such as missing user session.
 */
class UpdateProposalAPIRequestError extends Error {
  public static readonly UserSessionRequiredMessage = "User session is required to create UpdateProposalAPIRequest";

  public constructor(message = UpdateProposalAPIRequestError.UserSessionRequiredMessage) {
    super(message);
    this.name = "UpdateProposalAPIRequestError";
  }
}

export { UpdateProposalAPIRequest, UpdateProposalAPIRequestError };
